import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { ESTADO_COLORS, TIPO_COLORS, TIPO_LABELS, CONFIANZA_COLORS, CONFIANZA_LABELS, Badge } from '../utils/mantenimientoBadges';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMoneda = (n) => n == null
    ? '—'
    : n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

const RECOMENDACION_LABELS = {
    reparar: 'Seguir reparando',
    reemplazar: 'Evaluar reemplazo',
    mantenimiento_preventivo: 'Reforzar mantenimiento preventivo',
    monitorear: 'Monitorear',
};

// ── Componente ────────────────────────────────────────────────────────────────

// Ficha del activo: header con datos del ítem, stats calculados por el backend
// (sin IA), historia clínica y el diagnóstico de IA -- bajo demanda, nunca
// automático al abrir (ver el análisis en propuesta-mantenimiento-ia.html).
const FichaActivoDialog = ({ visible, onHide, itemMantenimientoId, administracionId }) => {
    const HOST_API = process.env.REACT_APP_URL;

    const [ficha, setFicha] = useState(null);
    const [loading, setLoading] = useState(false);
    const [analizando, setAnalizando] = useState(false);
    const [error, setError] = useState(null);

    const fetchFicha = useCallback(async () => {
        if (!itemMantenimientoId || !administracionId) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/mantenimiento/GetFichaActivo?itemMantenimientoId=${itemMantenimientoId}&administracionId=${administracionId}`
            );
            setFicha(data);
        } catch {
            setError('No se pudo cargar la ficha del activo.');
        } finally {
            setLoading(false);
        }
    }, [itemMantenimientoId, administracionId, HOST_API]);

    useEffect(() => {
        if (visible) fetchFicha();
        else { setFicha(null); setError(null); }
    }, [visible, fetchFicha]);

    const handleAnalizar = async () => {
        setAnalizando(true);
        try {
            const { data } = await axios.post(`${HOST_API}api/mantenimiento/AnalizarActivo`, {
                itemMantenimientoId,
                administracionId,
            });
            // El backend guarda el análisis; lo reflejamos al toque sin esperar
            // a recargar toda la ficha.
            setFicha(prev => prev ? { ...prev, ultimoDiagnostico: data } : prev);
        } catch {
            setError('No se pudo analizar el activo con IA. Revisá que la cuenta de Anthropic tenga crédito cargado.');
        } finally {
            setAnalizando(false);
        }
    };

    const activo = ficha?.activo;
    const stats = ficha?.stats;
    const diagnostico = ficha?.ultimoDiagnostico;

    return (
        <Dialog
            header={activo ? activo.nombre : 'Ficha del activo'}
            visible={visible}
            onHide={onHide}
            style={{ width: '760px' }}
            breakpoints={{ '960px': '100vw' }}
            modal
        >
            {loading && (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                    <ProgressSpinner style={{ width: 40, height: 40 }} />
                </div>
            )}

            {!loading && error && <Message severity="error" text={error} className="w-full mb-3" />}

            {!loading && ficha && (
                <div className="flex flex-column gap-4">

                    {/* Header del activo */}
                    <div style={{ fontSize: '0.88rem', color: '#667085' }}>
                        {[activo.marca, activo.modelo].filter(Boolean).join(' ')}
                        {activo.ubicacion ? ` · ${activo.ubicacion}` : ''}
                        {activo.nombreBarrio ? ` · ${activo.nombreBarrio}` : ''}
                    </div>

                    {/* Stats */}
                    <div className="grid" style={{ margin: 0 }}>
                        {[
                            ['Intervenciones totales', stats.totalIntervenciones],
                            [`Correctivas · ${stats.ventanaDiasAlerta} días`, stats.correctivasEnVentana],
                            ['Costo acumulado (12 meses)', fmtMoneda(stats.costoAcumulado12Meses)],
                            ['Último responsable', stats.ultimoResponsable || '—'],
                        ].map(([label, valor]) => (
                            <div key={label} className="col-6 md:col-3" style={{ padding: '0.4rem' }}>
                                <div style={{
                                    background: '#fff', border: '1px solid #eaecf0', borderRadius: 12,
                                    padding: '0.85rem 1rem', height: '100%',
                                }}>
                                    <div style={{ fontSize: '0.7rem', color: '#667085', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
                                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#101828', marginTop: 4 }}>{valor}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Alerta + botón de análisis */}
                    {stats.alerta && (
                        <Message
                            severity="warn"
                            className="w-full"
                            content={
                                <div className="flex justify-content-between align-items-center w-full gap-3 flex-wrap">
                                    <span>
                                        <strong>Falla recurrente:</strong> {stats.correctivasEnVentana} correctivos en los últimos {stats.ventanaDiasAlerta} días.
                                    </span>
                                    <Button
                                        label={analizando ? 'Analizando…' : (diagnostico ? 'Analizar de nuevo' : 'Analizar con IA')}
                                        icon="pi pi-sparkles"
                                        size="small"
                                        loading={analizando}
                                        onClick={handleAnalizar}
                                    />
                                </div>
                            }
                        />
                    )}
                    {!stats.alerta && (
                        <div>
                            <Button
                                label={analizando ? 'Analizando…' : (diagnostico ? 'Analizar de nuevo' : 'Analizar con IA')}
                                icon="pi pi-sparkles"
                                size="small"
                                severity="secondary"
                                outlined
                                loading={analizando}
                                onClick={handleAnalizar}
                            />
                        </div>
                    )}

                    {/* Diagnóstico de IA */}
                    {diagnostico && (
                        <div style={{ background: '#faf8ff', border: '1px solid #e4defc', borderRadius: 12, padding: '1rem 1.1rem' }}>
                            <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                <span style={{ fontWeight: 700, color: '#101828' }}>Diagnóstico de IA</span>
                                <span style={{ fontSize: '0.72rem', color: '#98a2b3' }}>
                                    {fmtFecha(diagnostico.fechaAnalisis)} · {diagnostico.modelo}
                                </span>
                            </div>
                            <p style={{ fontSize: '0.9rem', color: '#344054', margin: '0 0 .75rem' }}>{diagnostico.resumen}</p>
                            <div className="flex align-items-center gap-2 mb-3">
                                <span style={{ fontSize: '0.78rem', color: '#667085' }}>Recomendación:</span>
                                <strong style={{ fontSize: '0.9rem' }}>{RECOMENDACION_LABELS[diagnostico.recomendacion] || diagnostico.recomendacion}</strong>
                            </div>
                            <div className="flex flex-column gap-2">
                                {diagnostico.causasProbables?.map((c, i) => (
                                    <div key={i} className="flex justify-content-between align-items-center" style={{
                                        background: '#fff', border: '1px solid #eaecf0', borderRadius: 10, padding: '.5rem .75rem', fontSize: '.85rem',
                                    }}>
                                        <span>{c.causa}</span>
                                        <Badge label={CONFIANZA_LABELS[c.confianza] || c.confianza} colors={CONFIANZA_COLORS[c.confianza] || CONFIANZA_COLORS.baja} />
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: '0.72rem', color: '#98a2b3', margin: '.75rem 0 0' }}>
                                Generado por IA a partir de la historia clínica de este activo — es una sugerencia, no reemplaza la evaluación de un técnico.
                            </p>
                        </div>
                    )}

                    {/* Historia clínica */}
                    <div>
                        <div style={{ fontWeight: 700, color: '#101828', marginBottom: '.6rem' }}>Historia clínica</div>
                        {ficha.historial.length === 0 && (
                            <p style={{ color: '#98a2b3', fontSize: '.88rem' }}>Todavía no hay tareas registradas para este activo.</p>
                        )}
                        <div className="flex flex-column gap-2">
                            {ficha.historial.map((m) => (
                                <div key={m.mantenimientoId} style={{
                                    border: '1px solid #eaecf0', borderRadius: 10, padding: '.65rem .85rem',
                                    borderLeft: `3px solid ${m.tipo === 'Correctivo' ? '#f04438' : m.tipo === 'Preventivo' ? '#175cd3' : '#5925dc'}`,
                                }}>
                                    <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                                        <span style={{ fontSize: '.8rem', color: '#667085', fontWeight: 600 }}>{fmtFecha(m.fecha)}</span>
                                        <div className="flex gap-2">
                                            <Badge label={TIPO_LABELS[m.tipo] || m.tipo} colors={TIPO_COLORS[m.tipo] || TIPO_COLORS['Correctivo']} />
                                            <Badge label={m.estado} colors={ESTADO_COLORS[m.estado] || ESTADO_COLORS['Pendiente']} />
                                        </div>
                                    </div>
                                    <p style={{ margin: '.4rem 0 0', fontSize: '.87rem', color: '#344054' }}>{m.descripcion}</p>
                                    <div className="flex justify-content-between" style={{ marginTop: '.4rem', fontSize: '.78rem', color: '#667085' }}>
                                        <span>{m.responsable || '—'}</span>
                                        <strong style={{ color: '#101828' }}>{fmtMoneda(m.costo)}</strong>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default FichaActivoDialog;
